// src/hooks/useUserProfile.jsx
import { useEffect, useRef, useState } from "react";
import { db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import { 
  isApprovedCoopMember, 
  isPendingCoopMember, 
  hasCoopMembership,
  COOP_STATUS 
} from "@/lib/coopMembers.js";

export default function useUserProfile() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [coopMember, setCoopMember] = useState(null);
  const [error, setError] = useState(null);
  const unsubRef = useRef(null);
  const coopUnsubRef = useRef(null);
  const startedRef = useRef(null);

  // Listen to users/{uid} for basic user data
  useEffect(() => {
    // Wait for both user and auth loading to be resolved
    if (!user?.uid || authLoading) {
      setProfile(null);
      setCoopMember(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    let userUnsub = null;
    let memberUnsub = null;

    const setupListeners = () => {
      try {
        const userRef = doc(db, "users", user.uid);
        userUnsub = onSnapshot(
          userRef,
          (snap) => {
            if (!isMounted) return;
            
            if (snap.exists()) {
              const userData = snap.data();
              setProfile(userData);
            } else {
              setProfile(null);
            }
            setLoading(false);
          },
          (error) => {
            if (!isMounted) return;
            
            // Only log non-permission errors to reduce console noise
            if (error.code !== 'permission-denied') {
              console.error('Error fetching user profile:', error);
            }
            // Don't break the UI for permission issues
            setProfile(null);
            setLoading(false);
          }
        );

        // Listen to members/{uid} for cooperative membership data
        const memberRef = doc(db, "members", user.uid);
        memberUnsub = onSnapshot(
          memberRef,
          (snap) => {
            if (!isMounted) return;
            
            if (snap.exists()) {
              const memberData = snap.data();
              // Check if this is a REAL cooperative membership (has class, status, and proper structure)
              // A document without class/status is just a basic profile, not a coop membership
              if (memberData.class && memberData.status && 
                  (memberData.status === COOP_STATUS.PENDING || 
                   memberData.status === COOP_STATUS.APPROVED || 
                   memberData.status === COOP_STATUS.REJECTED || 
                   memberData.status === COOP_STATUS.SUSPENDED)) {
                setCoopMember(memberData);
              } else {
                setCoopMember(null);
              }
            } else {
              setCoopMember(null);
            }
            setLoading(false);
          },
          (error) => {
            if (!isMounted) return;
            
            // Only log non-permission errors to reduce console noise
            if (error.code !== 'permission-denied') {
              console.error('Error fetching cooperative membership:', error);
            }
            // Don't break the UI for permission issues
            setCoopMember(null);
            setLoading(false);
          }
        );
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up profile listeners:', error);
        setProfile(null);
        setCoopMember(null);
        setLoading(false);
      }
    };

    setupListeners();

    return () => {
      isMounted = false;
      if (userUnsub) userUnsub();
      if (memberUnsub) memberUnsub();
    };
  }, [user?.uid, authLoading]);

  // Additional cleanup effect to ensure profile is cleared when auth state changes
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setCoopMember(null);
      setLoading(false);
    }
  }, [user]);

  // Compute derived membership properties
  const isCoopMember = Boolean(coopMember);
  const isApprovedMember = Boolean(coopMember?.status === COOP_STATUS.APPROVED);
  const isPendingMember = Boolean(coopMember?.status === COOP_STATUS.PENDING);
  const isRejectedMember = Boolean(coopMember?.status === COOP_STATUS.REJECTED);
  const isSuspendedMember = Boolean(coopMember?.status === COOP_STATUS.SUSPENDED);
  
  // Legacy membership status for backward compatibility
  const membershipStatus = coopMember?.status || 'none';
  
  return { 
    loading, 
    profile, 
    coopMember,
    isCoopMember,
    isApprovedMember,
    isPendingMember,
    membershipStatus,
    error 
  };
}
